import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDemo } from '@/contexts/DemoContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Play, Clock, Calendar, Users, ListChecks, GitBranch, RefreshCw, Loader2, X } from 'lucide-react';
import workitectLogo from '@/assets/workitect-full-logo.png';

export function DemoIntroSlide() {
  const { isDemoActive, currentScenario, showIntro, dismissIntro, demoRuntimeMinutes, isPreparing, startError, retryStart, exitDemo, prepRestoreFailed, forceExitDemo } = useDemo();
  const canBegin = !isPreparing && !startError;
  useEffect(() => {
    if (!showIntro) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.key === 'Enter' || e.key === ' ') && canBegin) {
        e.preventDefault();
        dismissIntro();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showIntro, dismissIntro, canBegin]);

  if (!isDemoActive || !currentScenario || !showIntro) return null;

  const personas = Object.values(currentScenario.personas);
  const isDaily = currentScenario.scheduleMode === 'daily';
  const totalSteps = currentScenario.storyPoints.length;
  const perStepSec = Math.round((demoRuntimeMinutes * 60) / totalSteps);

  const overlay = (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-background/95 backdrop-blur-sm">
      {/* Content card */}
      <div className="w-full max-w-2xl mx-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={workitectLogo} alt="the Workitect" className="h-6 mx-auto mb-6 opacity-60" />
          <h1 className="text-3xl font-bold text-foreground tracking-tight mb-3">
            {currentScenario.name}
          </h1>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-lg mx-auto">
            {currentScenario.description}
          </p>
        </div>

        {/* Stats row */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span className="font-medium text-foreground">{demoRuntimeMinutes} min</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <Badge variant="outline" className="text-[10px] h-5 px-2 capitalize">
              {isDaily ? 'Daily' : 'Hourly'} scheduling
            </Badge>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ListChecks className="h-3.5 w-3.5" />
            <span>{currentScenario.tasks.length} tasks</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <GitBranch className="h-3.5 w-3.5" />
            <span>{totalSteps} steps</span>
            <span className="text-muted-foreground/60">~{perStepSec}s each</span>
          </div>
        </div>

        {/* Cast of characters */}
        <div className="rounded-xl border border-border bg-card p-5 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-4 w-4 text-muted-foreground" />
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Cast of Characters</h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {personas.map(persona => (
              <div
                key={persona.slotKey}
                className="flex items-center gap-3 rounded-lg border border-border/50 bg-muted/30 px-3 py-2.5 transition-colors hover:bg-muted/50"
              >
                <img
                  src={persona.avatarUrl}
                  alt={persona.name}
                  className="w-9 h-9 rounded-full object-cover border-2 border-background flex-shrink-0"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground truncate">{persona.name}</p>
                  <Badge
                    variant="outline"
                    className="text-[9px] h-4 px-1.5 capitalize mt-0.5"
                  >
                    {persona.role}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>

        {startError && (
          <div role="alert" className="mb-4 rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-foreground">
            <p className="font-medium">The walkthrough could not start.</p>
            <p className="mt-1 text-muted-foreground">{startError}</p>
          </div>
        )}
        <div className="flex justify-center items-center gap-3 mb-6">
          {startError ? (
            <>
              <Button size="lg" onClick={retryStart} className="gap-2 h-11 text-sm font-semibold">
                <RefreshCw className="h-4 w-4" /> Retry
              </Button>
              <Button size="lg" variant="outline" onClick={exitDemo} className="gap-2 h-11 text-sm">
                <X className="h-4 w-4" /> Exit
              </Button>
              {prepRestoreFailed && (
                <Button size="lg" variant="ghost" onClick={forceExitDemo} className="h-11 text-sm">
                  Leave without restoring
                </Button>
              )}
            </>
          ) : (
            <>
              <Button size="lg" onClick={dismissIntro} className="gap-2 px-8 h-11 text-sm font-semibold" disabled={!canBegin}>
                {isPreparing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                {isPreparing ? 'Preparing…' : 'Begin Walkthrough'}
              </Button>
              <Button size="lg" variant="outline" onClick={exitDemo} className="gap-2 h-11 text-sm">
                <X className="h-4 w-4" /> Exit
              </Button>
            </>
          )}
        </div>

        {/* Disclaimer */}
        <p className="text-[10px] text-muted-foreground/60 text-center leading-relaxed max-w-md mx-auto">
          Task elements are only placeholders used to demonstrate features and are not necessarily representative of real project artifacts or workflows.
        </p>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}
